import { useState } from 'react'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useAdminCourses } from '@/services/admin/courses.service'
import {
  useAdminModules,
  useCreateModule,
  useUpdateModule,
  useDeleteModule,
  Module,
  CreateModuleDTO,
  UpdateModuleDTO,
} from '@/services/admin/modules.service'
import { ModuleFormDialog } from './ModuleFormDialog'
import { useAuth } from '@/hooks/use-auth'
import { canManageCourseResource } from '@/types/auth'
import { getErrorMessage } from '@/utils/error'

const statusLabels = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
}

const statusColors = {
  draft: 'secondary',
  published: 'default',
  archived: 'outline',
} as const

export function ModulesManagement() {
  const { toast } = useToast()
  const { user } = useAuth()
  const [selectedCourseId, setSelectedCourseId] = useState<number>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingModule, setEditingModule] = useState<Module | null>(null)
  const [deletingModule, setDeletingModule] = useState<Module | null>(null)

  const { data: courses } = useAdminCourses()
  const { data: modules, isLoading } = useAdminModules(selectedCourseId)
  const createModule = useCreateModule()
  const updateModule = useUpdateModule()
  const deleteModule = useDeleteModule()

  const handleCreate = () => {
    setEditingModule(null)
    setDialogOpen(true)
  }

  const handleEdit = (module: Module) => {
    setEditingModule(module)
    setDialogOpen(true)
  }

  const handleDelete = (module: Module) => {
    setDeletingModule(module)
  }

  const confirmDelete = async () => {
    if (!deletingModule) return

    try {
      await deleteModule.mutateAsync(deletingModule.id)
      toast({
        title: 'Módulo deletado',
        description: `O módulo "${deletingModule.title}" foi removido com sucesso.`,
      })
      setDeletingModule(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar módulo',
        description: getErrorMessage(error, 'Ocorreu um erro ao deletar o módulo.'),
      })
    }
  }

  const handleSubmit = async (data: CreateModuleDTO | UpdateModuleDTO) => {
    try {
      if (editingModule) {
        await updateModule.mutateAsync({
          id: editingModule.id,
          data: data as UpdateModuleDTO,
        })
        toast({
          title: 'Módulo atualizado',
          description: 'O módulo foi atualizado com sucesso.',
        })
      } else {
        await createModule.mutateAsync(data as CreateModuleDTO)
        toast({
          title: 'Módulo criado',
          description: 'O módulo foi criado com sucesso.',
        })
      }
      setDialogOpen(false)
      setEditingModule(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: editingModule ? 'Erro ao atualizar módulo' : 'Erro ao criar módulo',
        description: getErrorMessage(error, 'Ocorreu um erro ao processar a solicitação.'),
      })
      throw error
    }
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Gerenciamento de Módulos</CardTitle>
              <CardDescription>Crie, edite e gerencie os módulos dos cursos</CardDescription>
            </div>
            <Button onClick={handleCreate} disabled={!courses || courses.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Módulo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Course Filter */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filtrar por curso:</label>
              <Select
                value={selectedCourseId?.toString() || 'all'}
                onValueChange={(value) =>
                  setSelectedCourseId(value === 'all' ? undefined : Number(value))
                }
              >
                <SelectTrigger className="w-full md:w-[300px]">
                  <SelectValue placeholder="Todos os cursos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os cursos</SelectItem>
                  {courses?.map((course) => (
                    <SelectItem key={course.id} value={course.id.toString()}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !modules || modules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum módulo cadastrado{selectedCourseId ? ' para este curso' : ''}.</p>
              <p className="text-sm mt-2">Clique em "Novo Módulo" para começar.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Curso</TableHead>
                    <TableHead>Ordem</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Episódios</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modules.map((module) => (
                    <TableRow key={module.id}>
                      <TableCell>
                        <div className="font-medium">{module.title}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {module.course?.title}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">#{module.order}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusColors[module.status]}>
                          {statusLabels[module.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {module._count?.episodes || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageCourseResource(user?.role, module.course?.companyId) ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(module)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(module)}
                              title="Deletar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Somente leitura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ModuleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        module={editingModule}
        preselectedCourseId={selectedCourseId}
        onSubmit={handleSubmit}
        isSubmitting={createModule.isPending || updateModule.isPending}
      />

      <AlertDialog open={!!deletingModule} onOpenChange={() => setDeletingModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso irá deletar permanentemente o módulo "
              {deletingModule?.title}" e todos os seus episódios.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteModule.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
